<template>
  <div
    ref="root"
    class="plan-usage"
    @mouseenter="open"
    @mouseleave="scheduleClose"
    @focusin="open"
    @focusout="handleFocusout"
    @keydown.esc="handleEscape"
    @pointerdown.capture="handlePointerdown"
    @click.capture="handleClickCapture"
  >
    <slot :popover-id="popoverId" />

    <div
      v-if="isOpen"
      :id="popoverId"
      class="plan-usage__card"
      :style="cardStyle"
      role="dialog"
      :aria-label="$t('billingYourTariffShortForm')"
    >
      <section class="plan-usage__section">
        <div v-if="!leads.isCurrentUnlimited" class="plan-usage__meta">
          <span class="plan-usage__title">{{ $t('billingLeadsAvailableLabel') }}</span>
          <span v-if="leads.isLoading" class="plan-usage__value plan-usage__value--muted">{{ $t('tariffUsageLoadingLabel') }}</span>
          <span v-else class="plan-usage__total">{{ leads.isUnlimited ? $t('pricingComparisonUnlimited') : leads.total }}</span>
        </div>

        <div
          v-for="source in leads.sources"
          :key="source.key"
          :class="['plan-usage__source', `plan-usage__source--${source.key}`]"
        >
          <div class="plan-usage__meta">
            <span :id="`${popoverId}-${source.key}`" class="plan-usage__name">
              <svg v-if="source.key === 'tariff'" class="plan-usage__icon" width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M18.3566 6.90656C18.538 6.9285 18.7085 7.00109 18.8464 7.11511C18.9847 7.22847 19.0843 7.3783 19.1326 7.54553C19.1809 7.71277 19.1757 7.88985 19.1176 8.05425L16.2911 16.2666C16.2627 16.3494 16.215 16.4251 16.1515 16.488C16.088 16.5508 16.0105 16.5994 15.9245 16.6299C15.8386 16.6605 15.7465 16.6723 15.6551 16.6645C15.5637 16.6567 15.4753 16.6294 15.3965 16.5848C15.379 16.5746 13.4256 15.5072 9.9972 15.5072C6.56876 15.5072 4.61543 16.5746 4.59633 16.5855C4.51758 16.6297 4.42936 16.6566 4.33818 16.6642C4.24701 16.6717 4.15521 16.6597 4.06956 16.6291C3.98392 16.5985 3.90661 16.5501 3.84335 16.4873C3.78009 16.4246 3.7325 16.3492 3.70408 16.2666L0.8776 8.05208C0.82111 7.88772 0.81705 7.7112 0.865931 7.54467C0.914812 7.37813 1.01446 7.229 1.15236 7.11599C1.29026 7.00298 1.46028 6.93112 1.64108 6.90944C1.82188 6.88775 2.0054 6.9172 2.16861 6.99409L6.0027 8.80571L9.20502 3.75783C9.28734 3.62836 9.40353 3.52125 9.54233 3.44691C9.68112 3.37256 9.83781 3.3335 9.9972 3.3335C10.1566 3.3335 10.3133 3.37256 10.4521 3.44691C10.5909 3.52125 10.7071 3.62836 10.7894 3.75783L13.9917 8.80353L17.8273 6.99192C17.9909 6.91434 18.1751 6.88463 18.3566 6.90656Z" fill="currentColor"/>
              </svg>
              <svg v-else-if="source.key === 'other-tariffs'" class="plan-usage__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M8 2L14 5L8 8L2 5L8 2Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>
                <path d="M2 8L8 11L14 8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2 11L8 14L14 11" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span v-else class="plan-usage__icon plan-usage__icon--gift" aria-hidden="true"></span>
              <span class="plan-usage__label" :title="source.label">{{ source.label }}</span>
            </span>
            <span v-if="source.isUnlimited" class="plan-usage__value">{{ $t('pricingComparisonUnlimited') }}</span>
            <span v-else-if="source.total === null" class="plan-usage__value">{{ source.remaining }}</span>
            <span v-else class="plan-usage__value">{{ $t('billingLeadsRemainingOfLabel', { remaining: source.remaining, total: source.total }) }}</span>
          </div>
          <div
            v-if="source.percent !== null"
            class="plan-usage__bar"
            role="progressbar"
            :aria-labelledby="`${popoverId}-${source.key}`"
            aria-valuemin="0"
            :aria-valuemax="source.total"
            :aria-valuenow="source.remaining"
          >
            <span :style="{ width: `${source.percent}%` }"></span>
          </div>
        </div>
      </section>

      <section v-if="team" class="plan-usage__section plan-usage__section--team">
        <div class="plan-usage__meta">
          <span class="plan-usage__title">{{ $t('planUsageTeamMembersLabel') }}</span>
          <TariffLockBadge
            v-if="team.isLocked"
            :gate="teamGate"
            :feature-key="teamFeatureKey"
            source="plan-usage-popover"
          />
          <span v-else-if="team.isLoading" class="plan-usage__value plan-usage__value--muted">{{ $t('tariffUsageLoadingLabel') }}</span>
          <span v-else-if="team.used === null" class="plan-usage__value plan-usage__value--muted">{{ $t('tariffUsageUnavailableLabel') }}</span>
          <span v-else-if="team.isUnlimited" class="plan-usage__value">{{ team.used }} / {{ $t('pricingComparisonUnlimited') }}</span>
          <span v-else-if="team.limit !== null" class="plan-usage__value">{{ team.used }} / {{ team.limit }}</span>
          <span v-else class="plan-usage__value">{{ team.used }}</span>
        </div>
        <div v-if="!team.isLocked && !team.isLoading" class="plan-usage__team">
          <svg class="plan-usage__icon plan-usage__icon--team" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 21a8 8 0 0 0-16 0" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="10" cy="8" r="5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span
            v-for="member in team.members"
            :key="member.id"
            class="plan-usage__avatar"
            :title="member.name || member.email"
          >{{ member.letter }}</span>
          <span v-if="team.overflow" class="plan-usage__avatar plan-usage__avatar--more">+{{ team.overflow }}</span>
          <router-link
            v-if="team.canInvite"
            :to="{ name: 'team-view' }"
            class="plan-usage__avatar plan-usage__avatar--add"
            :aria-label="$t('addNewPerson')"
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M9.9987 4.3335V10.0002M9.9987 10.0002V15.6668M9.9987 10.0002H15.6654M9.9987 10.0002L4.33203 10.0002" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </router-link>
        </div>
      </section>

      <div class="plan-usage__footer">
        <router-link :to="{ name: 'tariffs-page' }" class="plan-usage__manage">{{ $t('planUsageManagePlanLabel') }}</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import TariffLockBadge from '@/components/tariff/TariffLockBadge.vue'
import { useTariffGate } from '@/composables/useTariffGate'
import { useUserStore } from '@/store/userStore'
import { useTariffStore } from '@/store/tariffStore'
import { useTeamStore } from '@/store/teamStore'
import { TARIFF_FEATURE_KEYS, TARIFF_GATE_ACTIONS, TARIFF_GATE_STATES } from '@/constants/tariffFeatureKeys'
import { getLeadOverview, getTariffDisplayLeadSummary, isDisplayUnlimitedTariff } from '@/utils/leadConsumption'
import { getFeatureUsage } from '@/services/tariffs/tariffUsageResolver'
import { toSafeNumber } from '@/services/tariffs/tariffValueNormalizer'

const CLOSE_DELAY_MS = 120
const NARROW_VIEWPORT_PX = 480
const MAX_VISIBLE_MEMBERS = 3
const TEAM_LOCKED_STATES = [TARIFF_GATE_STATES.UNAVAILABLE, TARIFF_GATE_STATES.DELETE_ONLY, TARIFF_GATE_STATES.GRANDFATHERED, TARIFF_GATE_STATES.READ_ONLY]

let instanceCounter = 0

function toPercent(used, limit) {
  return Math.min(100, Math.max(0, (used / limit) * 100))
}

// Each lead source gets its own bar on its own scale (remaining of its own
// total). Aggregates without a truthful denominator get no bar.
function buildLeadSource(key, label, balance) {
  const total = balance.isUnlimited ? null : balance.total
  return {
    key,
    label,
    isUnlimited: balance.isUnlimited,
    total,
    remaining: balance.remaining,
    percent: total > 0 ? toPercent(balance.remaining, total) : null,
  }
}

export default {
  name: 'PlanUsagePopover',
  components: { TariffLockBadge },
  data() {
    instanceCounter += 1
    return {
      isOpen: false,
      popoverId: `plan-usage-popover-${instanceCounter}`,
      suppressNextClick: false,
      cardStyle: null,
      teamFeatureKey: TARIFF_FEATURE_KEYS.USER_TEAM,
    }
  },
  computed: {
    userStore() {
      return useUserStore()
    },
    tariffStore() {
      return useTariffStore()
    },
    teamStore() {
      return useTeamStore()
    },
    teamGate() {
      return useTariffGate({
        featureKey: TARIFF_FEATURE_KEYS.USER_TEAM,
        action: TARIFF_GATE_ACTIONS.READ,
      })
    },
    teamInviteGate() {
      return useTariffGate({
        featureKey: TARIFF_FEATURE_KEYS.USER_TEAM,
        action: TARIFF_GATE_ACTIONS.CREATE,
      })
    },
    leads() {
      if (!this.userStore.isUserDataLoaded) {
        return { isLoading: true, sources: [] }
      }

      const overview = getLeadOverview({ tarifs: this.userStore.tarifs, bonuses: this.userStore.bonuses })
      const sources = []
      const current = getTariffDisplayLeadSummary(overview.currentTariff)
      if (current) {
        sources.push(buildLeadSource('tariff', overview.currentTariff.name, {
          isUnlimited: current.isUnlimited,
          total: current.limit,
          remaining: current.remaining,
        }))
      }
      const others = overview.nonCurrentTariffs
      if (others.count) {
        sources.push(buildLeadSource('other-tariffs', this.$t('billingGeneralLeadsFromOtherTarifs'), {
          isUnlimited: others.isUnlimited,
          total: null,
          remaining: others.remaining,
        }))
      }
      if (overview.bonus.count) {
        sources.push(buildLeadSource('bonus', this.$t('billingGeneralBonus'), overview.bonus))
      }

      return {
        isLoading: false,
        isUnlimited: overview.isUnlimited || isDisplayUnlimitedTariff(overview.currentTariff),
        // An unlimited current plan already says it all in its own row.
        isCurrentUnlimited: Boolean(current?.isUnlimited),
        total: overview.totalRemaining,
        sources,
      }
    },
    // Team numbers come only from the backend `user.team` gate / usage row;
    // members are never counted from Team rows (frozen members still take a slot).
    team() {
      const gate = this.teamGate
      if (gate.state === TARIFF_GATE_STATES.LOADING) {
        return { isLoading: true }
      }
      if (TEAM_LOCKED_STATES.includes(gate.state)) {
        return { isLocked: true }
      }

      const usage = getFeatureUsage(this.tariffStore.featureUsage, TARIFF_FEATURE_KEYS.USER_TEAM)
      const used = usage.isKnown ? toSafeNumber(usage.rawUsed) : null
      const isUnlimited = gate.isUnlimited
      const limit = !isUnlimited && gate.limit > 0 ? gate.limit : null

      // Only already-loaded members are shown; the popover never fetches them.
      const loaded = this.teamStore.isMembersLoaded ? this.teamStore.members.filter((member) => !member.isNew) : []
      const visibleCount = Math.min(MAX_VISIBLE_MEMBERS, loaded.length, used ?? loaded.length)
      const members = loaded.slice(0, visibleCount).map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        letter: (member.name || member.email || '?').trim().charAt(0).toUpperCase(),
      }))

      return {
        used,
        limit,
        isUnlimited,
        members,
        overflow: used !== null ? Math.max(0, used - members.length) : 0,
        canInvite: this.teamInviteGate.allowed,
      }
    },
  },
  mounted() {
    document.addEventListener('pointerdown', this.handleDocumentPointerdown)
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.handleDocumentPointerdown)
    clearTimeout(this.closeTimer)
  },
  methods: {
    open() {
      clearTimeout(this.closeTimer)
      if (!this.isOpen) {
        this.cardStyle = this.getNarrowCardStyle()
      }
      this.isOpen = true
    },
    // On narrow screens the card is pinned to the viewport with side gutters,
    // so it never overflows when the trigger is not at the right edge.
    getNarrowCardStyle() {
      if (window.innerWidth > NARROW_VIEWPORT_PX || !this.$refs.root) {
        return null
      }
      const { bottom } = this.$refs.root.getBoundingClientRect()
      return { position: 'fixed', top: `${Math.round(bottom) + 8}px`, left: '16px', right: '16px', width: 'auto', maxWidth: 'none' }
    },
    close() {
      clearTimeout(this.closeTimer)
      this.isOpen = false
    },
    scheduleClose() {
      clearTimeout(this.closeTimer)
      this.closeTimer = setTimeout(this.close, CLOSE_DELAY_MS)
    },
    handleFocusout(event) {
      if (!this.$refs.root?.contains(event.relatedTarget)) {
        this.close()
      }
    },
    handleEscape() {
      if (this.isOpen) {
        this.close()
      }
    },
    handleDocumentPointerdown(event) {
      if (this.isOpen && !this.$refs.root?.contains(event.target)) {
        this.close()
      }
    },
    handlePointerdown(event) {
      if (event.pointerType === 'touch' && !this.isOpen) {
        this.suppressNextClick = true
      }
    },
    handleClickCapture(event) {
      if (this.suppressNextClick) {
        this.suppressNextClick = false
        event.stopPropagation()
        event.preventDefault()
        this.open()
        return
      }
      this.close()
    },
  },
}
</script>

<style scoped>
.plan-usage {
  position: relative;
  display: inline-flex;
}

.plan-usage__card {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 50;
  width: 300px;
  max-width: calc(100vw - 32px);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.16);
  cursor: default;
}

.plan-usage__card::before {
  content: '';
  position: absolute;
  top: -9px;
  left: 0;
  right: 0;
  height: 9px;
}

.plan-usage__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.plan-usage__section--team,
.plan-usage__footer {
  padding-top: 16px;
  border-top: 1px solid var(--color-main-light-blue);
}

.plan-usage__meta + .plan-usage__source {
  margin-top: 4px;
}

.plan-usage__meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
}

.plan-usage__title {
  color: var(--color-main-black);
  font-weight: 600;
}

.plan-usage__total {
  color: var(--color-main-black);
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
}

.plan-usage__source {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  --plan-usage-accent: var(--color-main-blue);
  --plan-usage-track: var(--color-main-light-blue);
}

.plan-usage__source--tariff {
  background: var(--color-main-alice-blue);
}

.plan-usage__source--other-tariffs {
  background: var(--color-main-light-gray);
  --plan-usage-accent: var(--color-main-gray-settings);
}

.plan-usage__source--bonus {
  background: color-mix(in srgb, var(--color-additional-green) 10%, #FFFFFF);
  --plan-usage-accent: var(--color-additional-green);
  --plan-usage-track: color-mix(in srgb, var(--color-additional-green) 22%, #FFFFFF);
}

.plan-usage__name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--plan-usage-accent);
  font-weight: 600;
}

.plan-usage__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-usage__icon {
  flex: 0 0 auto;
  color: var(--plan-usage-accent, var(--color-main-blue));
}

.plan-usage__icon--gift {
  display: inline-block;
  width: 16px;
  height: 16px;
  background: var(--plan-usage-accent);
  -webkit-mask: url('~/public/img/icons/gift_black.svg') center / contain no-repeat;
  mask: url('~/public/img/icons/gift_black.svg') center / contain no-repeat;
}

.plan-usage__value {
  flex: 0 0 auto;
  color: var(--color-main-black);
  font-weight: 600;
  white-space: nowrap;
}

.plan-usage__value--muted {
  color: var(--color-main-dark-gray);
  font-weight: 400;
  font-size: 12px;
}

.plan-usage__bar {
  overflow: hidden;
  height: 6px;
  border-radius: 999px;
  background: var(--plan-usage-track);
}

.plan-usage__bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--plan-usage-accent);
}

.plan-usage__team {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.plan-usage__icon--team {
  margin-right: 2px;
}

.plan-usage__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-main-alice-blue);
  color: var(--color-main-blue);
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
}

.plan-usage__avatar--more {
  background: var(--color-main-light-gray);
  color: var(--color-main-gray-settings);
}

.plan-usage__avatar--add {
  background: transparent;
  border: 1px dashed var(--color-main-blue);
}

.plan-usage__avatar--add:hover,
.plan-usage__avatar--add:focus-visible {
  background: var(--color-main-alice-blue);
}

.plan-usage__manage {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border: 1px solid var(--color-main-blue);
  border-radius: 8px;
  color: var(--color-main-blue);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.3s ease;
}

.plan-usage__manage:hover,
.plan-usage__manage:focus-visible {
  background: linear-gradient(to right, #FFFFFF, #ECEFFF);
}
</style>
